import { z } from 'zod';

import { ConsumerOfVoiceCallSchema } from '../base/baseSchema.js';
import { BlockValueUuidSchema } from '../base/blockValueUuidSchema.js';
import { VoiceCallReturnDataSchema } from '../voiceCall/config.js';

export enum HumanTaskBlockWorkContextContentType {
  Ehr = 'Ehr',
  Url = 'Url',
  Pdf = 'Pdf',
  Markdown = 'Markdown',
  File = 'File',
  Portal = 'Portal',
  VoiceCall = 'VoiceCall',
  VoiceCallV2 = 'VoiceCallV2',
}

export const BaseHumanTaskWorkContextSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
});

export const HumanTaskWorkContextEhrConfigSchema = BaseHumanTaskWorkContextSchema.extend({
  contentType: z.literal(HumanTaskBlockWorkContextContentType.Ehr),
  ehrInstance: z.string(),
});
export type HumanTaskWorkContextEhrConfig = z.infer<typeof HumanTaskWorkContextEhrConfigSchema>;

export const HumanTaskWorkContextUrlConfigSchema = BaseHumanTaskWorkContextSchema.extend({
  contentType: z.literal(HumanTaskBlockWorkContextContentType.Url),
  url: z.string(),
});
export type HumanTaskWorkContextUrlConfig = z.infer<typeof HumanTaskWorkContextUrlConfigSchema>;

export const HumanTaskWorkContextPdfConfigSchema = BaseHumanTaskWorkContextSchema.extend({
  contentType: z.literal(HumanTaskBlockWorkContextContentType.Pdf),
  documentId: z.string(),
});
export type HumanTaskWorkContextPdfConfig = z.infer<typeof HumanTaskWorkContextPdfConfigSchema>;

export const HumanTaskWorkContextMarkdownConfigSchema = BaseHumanTaskWorkContextSchema.extend({
  contentType: z.literal(HumanTaskBlockWorkContextContentType.Markdown),
  markdownContent: z.string(),
});
export type HumanTaskWorkContextMarkdownConfig = z.infer<
  typeof HumanTaskWorkContextMarkdownConfigSchema
>;
export const HumanTaskWorkContextFileConfigSchema = BaseHumanTaskWorkContextSchema.extend({
  contentType: z.literal(HumanTaskBlockWorkContextContentType.File),
  fileReference: z.string(),
});
export type HumanTaskWorkContextFileConfig = z.infer<typeof HumanTaskWorkContextFileConfigSchema>;

export enum HumanTaskWorkContextPortal {
  Availity = 'Availity',
  Bcbsri = 'Bcbsri',
  Carelon = 'Carelon',
  CareCore = 'CareCore',
  Cohere = 'Cohere',
  MedSolutions = 'MedSolutions',
  RadMd = 'RadMD',
  Regal = 'Regal',
  UnitedHealthcare = 'Uhc',
}

export const HumanTaskWorkContextPortalConfigSchema = BaseHumanTaskWorkContextSchema.extend({
  contentType: z.literal(HumanTaskBlockWorkContextContentType.Portal),
  portal: z.enum(HumanTaskWorkContextPortal),
});
export const HumanTaskWorkContextVoiceCallConfigSchema = BaseHumanTaskWorkContextSchema.extend({
  contentType: z.literal(HumanTaskBlockWorkContextContentType.VoiceCall),
  voiceCall: ConsumerOfVoiceCallSchema({ isRequired: true }),
  callSummary: z.string(),
  voiceCallData: VoiceCallReturnDataSchema.optional(),
});
export const HumanTaskWorkContextVoiceCallV2ConfigSchema = BaseHumanTaskWorkContextSchema.extend({
  contentType: z.literal(HumanTaskBlockWorkContextContentType.VoiceCallV2),
  voiceCallId: BlockValueUuidSchema({ isRequired: true }),
});
export type HumanTaskWorkContextVoiceCallConfig = z.infer<
  typeof HumanTaskWorkContextVoiceCallConfigSchema
>;

export const HumanTaskWorkContextSchema = z.discriminatedUnion('contentType', [
  HumanTaskWorkContextEhrConfigSchema,
  HumanTaskWorkContextUrlConfigSchema,
  HumanTaskWorkContextPdfConfigSchema,
  HumanTaskWorkContextMarkdownConfigSchema,
  HumanTaskWorkContextFileConfigSchema,
  HumanTaskWorkContextPortalConfigSchema,
  HumanTaskWorkContextVoiceCallConfigSchema,
  HumanTaskWorkContextVoiceCallV2ConfigSchema,
]);
export type HumanTaskWorkContext = z.infer<typeof HumanTaskWorkContextSchema>;
